<script lang="ts">
	// The sell screen: the category rail, the item grid and the cart.
	//
	// Every tap changes a local number. Nothing here asks the server what an
	// order costs, because the same formula runs in `domain/pricing.ts` and is
	// held to the server's own golden vectors. The server reprices the whole
	// order at checkout regardless, so being fast here costs nothing.
	//
	// An item with a required option opens its editor instead of landing
	// in the cart, so a required answer is item, choice, Done: three taps.
	//
	// "My items" (UC-17) comes first in the rail: the cashier's own set, in
	// their order. The till opens on it after every unlock, which is why the
	// chosen entry belongs to the till rather than to this screen.
	//
	// Discount (UC-35) sits above the totals, off while the cart is empty, so a
	// sale with no discount keeps its taps. The cart then shows the discount
	// and what it takes off. A discount a manager typed stays through a lock;
	// while a cashier who is not a manager is signed in, the cart says only a
	// manager can give it, and Send stays off until it goes or a manager is
	// back.
	//
	// In a shop with tables (UC-39), Dine in asks which table: the cart shows
	// the table it picked, which a tap changes, or asks for one, and Send stays
	// off until a dine-in has its table. Takeaway stays the type a new cart
	// starts with, so a counter sale keeps its taps. The Till page draws the
	// ask on the flag ground, a tone Button does not have; the secondary
	// Button is a divergence the owner allowed (2026-10-12, #345), until the
	// design project draws one (docs/backlog.md).
	//
	// Every order is sent before it is paid (#348). Send puts the lines not
	// sent yet on the order: the first Send places it open, a later one adds
	// to it. Once sent, the cart's head names the order and offers New order,
	// its type, its table and its delivery details are the order's, and its
	// sent lines show above the new ones, marked Sent, with no Remove. A sent
	// line changes only by a line cancellation (#379): a tap opens Cancel
	// item, which the till puts over Sell. Pay is on only for a sent order with nothing waiting
	// to be sent, and says why while something is. The totals are the order's
	// lines as sold and the new ones, with the order's own discount and fee.
	//
	// A delivery names its driver (UC-4.R6, T33): its first Send with none
	// asks which, in one tap, and the cart then shows him, which a tap
	// changes. A sent delivery names him beside its customer. A new cart
	// starts with none picked, so no delivery goes with a driver nobody chose.
	//
	// A bill that did not print, or whose delivery is uncertain (#515), is said
	// by the till's notice under its header, across the whole page, with its
	// action: Print bill keeps its place beside Pay, and is off while the
	// store holds the bill for a decision. Lines not sent yet keep Send there,
	// so it blocks no sale.

	import Button from '../components/Button.svelte';
	import Band from '../components/Band.svelte';
	import CartLine from '../components/CartLine.svelte';
	import Loading from '../components/Loading.svelte';
	import MenuTile from '../components/MenuTile.svelte';
	import NavItem from '../components/NavItem.svelte';
	import Segmented from '../components/Segmented.svelte';
	import { fill, message, type Language } from '../i18n/messages';
	import { discountLabel } from '../format/discount';
	import { formatMinor } from '../format/money';
	import { orderNumber, tradingDayLabel } from '../format/order';
	import { normalizePhone } from '../domain/phone';
	import { recomputeTotals, type Line } from '../domain/pricing';
	import {
		addItem,
		currentDraft,
		draftTotals,
		setType,
		type DraftLine,
		type OrderType,
	} from '../draft/draft.svelte';
	import { asksFirst } from '../till/editor';
	import { lineName, soldByWeight, tilePrice } from '../till/weighed';
	import type { components } from '../api/types';
	import type { components as storeComponents } from '../api/store';

	type Menu = components['schemas']['MenuBody'];
	type Item = components['schemas']['ItemBody'];
	type Area = components['schemas']['AreaBody'];
	type Order = storeComponents['schemas']['OrderBody'];

	interface Props {
		language: Language;
		menu: Menu;
		/** A menu has arrived; before that, the grid shows the wait. */
		menuLoaded: boolean;
		minorDigits: number;
		deliveryFeeMinor: number;
		areas: Area[];
		countryCode: string;
		/** A Send got no answer that says what became of it: the cart is read-only. */
		pending: boolean;
		retrying: boolean;
		/** The sent order on Sell, as the till's store holds it, or null. */
		openOrder: Order | null;
		/** The trading day the store last named, so an older open order says its day. */
		today: string;
		/** A Send is in flight, and has run past 300 ms. */
		sending: boolean;
		sendSlow: boolean;
		onSend: () => void;
		onNewOrder: () => void;
		/** A sheet is open over the cart for payment: the cart holds still. */
		paying: boolean;
		onAsk: (item: Item) => void;
		onEditLine: (line: DraftLine) => void;
		/** Take a line out; the till offers the Undo. */
		onRemoveLine: (line: DraftLine) => void;
		onOpenDelivery: () => void;
		onPay: () => void;
		onPrintBill: () => void;
		printingBill: boolean;
		billChanged: boolean;
		/** The order's bill is held for a decision, as its delivery is uncertain (#515). */
		billHeld: boolean;
		onRetryPending: () => void;
		/** The cashier's own items, in their order. */
		mine: string[];
		/** The rail's chosen entry: 'mine' or a category id. */
		selected: string;
		onSelect: (entry: string) => void;
		onEditMine: () => void;
		/** The signed-in user is a manager, who may type a discount. */
		isManager: boolean;
		onDiscount: () => void;
		/** A sent line was tapped: Cancel item opens for it (UC-44). */
		onCancelLine: (line: components['schemas']['QuotedLineBody']) => void;
		/** The delivery's driver, as the till's list names him, or '' (UC-4.R6). */
		driverName: string;
		/** Why the cart asks again: the store wanted a driver, or the one it had is gone. */
		driverNotice: string;
		/** The cart's driver was tapped: Pick a driver opens to pick another. */
		onOpenDrivers: () => void;
		/** The dine-in's table, as the till's list names it, or '' (UC-39). */
		tableName: string;
		/** A dine-in in a shop with tables that has none yet: Pay waits for one. */
		tableAsk: boolean;
		/** Why the cart asks again: the server wanted a table, or the one it had is gone. */
		tableNotice: string;
		/** Dine in was tapped, or tapped again. */
		onDineIn: () => void;
		onOpenTables: () => void;
	}

	let {
		language,
		menu,
		menuLoaded,
		minorDigits,
		deliveryFeeMinor,
		areas,
		countryCode,
		pending,
		retrying,
		openOrder,
		today,
		sending,
		sendSlow,
		onSend,
		onNewOrder,
		paying,
		onAsk,
		onEditLine,
		onRemoveLine,
		onOpenDelivery,
		onPay,
		onPrintBill,
		printingBill,
		billChanged,
		billHeld,
		onRetryPending,
		mine,
		selected,
		onSelect,
		onEditMine,
		isManager,
		onDiscount,
		onCancelLine,
		driverName,
		driverNotice,
		onOpenDrivers,
		tableName,
		tableAsk,
		tableNotice,
		onDineIn,
		onOpenTables,
	}: Props = $props();

	const draft = $derived(currentDraft());
	const categories = $derived(menu.categories ?? []);
	const items = $derived(menu.items ?? []);
	const locked = $derived(pending || paying || sending);

	const MINE = 'mine';
	const category = $derived(selected || (categories[0]?.id ?? ''));
	const byId = $derived(new Map(items.map((item) => [item.id, item])));
	// An item the menu no longer has is left out; the server drops it too.
	const mineShown = $derived(
		mine.map((id) => byId.get(id)).filter((item): item is Item => item !== undefined),
	);
	const shown = $derived(
		category === MINE ? mineShown : items.filter((item) => item.categoryId === category),
	);

	// The order's lines as sold: each at the unit it was sold at, its choices
	// already in it.
	const sentLines = $derived<Line[]>(
		(openOrder?.lines ?? []).map((line) => ({
			itemId: line.itemId,
			name: line.name,
			unitBaseMinor: line.unitMinor,
			qty: line.qty,
			note: line.note ?? '',
		})),
	);
	const totals = $derived.by(() => {
		if (!openOrder)
			return draftTotals(draft.type === 'delivery' ? deliveryFeeMinor : 0, minorDigits);
		try {
			return recomputeTotals(
				[...sentLines, ...draft.lines],
				openOrder.deliveryFeeMinor,
				openOrder.discount ?? null,
				minorDigits,
			);
		} catch {
			return null;
		}
	});
	const total = $derived(formatMinor(totals?.totalMinor ?? 0, minorDigits));
	const discount = $derived(
		openOrder ? (openOrder.discount ?? null) : draft.lines.length > 0 ? draft.discount : null,
	);
	// A typed discount waits for a manager only before the first Send: one on
	// a sent order is the order's, and anyone may take its payment.
	const blocked = $derived(!openOrder && discount !== null && !discount.discountId && !isManager);
	const unsent = $derived(draft.lines.length);
	const sendOff = $derived(
		unsent === 0 || totals === null || locked || blocked || (!openOrder && tableAsk),
	);
	const payOff = $derived(!openOrder || unsent > 0 || locked || totals === null);
	const typeOf = $derived(openOrder?.type ?? draft.type);
	// The sent order's line under its number: its type, who a delivery goes
	// to, and the trading day it was sent on when that is not today.
	const orderMeta = $derived(
		openOrder
			? [
					message(
						language,
						openOrder.type === 'dine_in'
							? 'sellDineIn'
							: openOrder.type === 'delivery'
								? 'sellDeliveryType'
								: 'sellTakeaway',
					),
					openOrder.type === 'dine_in' && openOrder.tableName
						? fill(message(language, 'tableLabel'), openOrder.tableName)
						: '',
					openOrder.type === 'delivery' ? (openOrder.customerName ?? '') : '',
					openOrder.type === 'delivery'
						? openOrder.driverName
							? fill(message(language, 'driverLabel'), openOrder.driverName)
							: message(language, 'driverNone')
						: '',
					today && openOrder.tradingDay && openOrder.tradingDay !== today
						? fill(
								message(language, 'sellOrderFrom'),
								tradingDayLabel(openOrder.tradingDay, language),
							)
						: '',
				]
					.filter(Boolean)
					.join(' · ')
			: '',
	);

	// An item sold by weight always asks: its grams come first (#917).
	function add(item: Item) {
		if (soldByWeight(item) || asksFirst(menu, item.id)) onAsk(item);
		else addItem(item.id, item.name, item.priceMinor);
	}

	function countIn(categoryId: string): number {
		return items.filter((item) => item.categoryId === categoryId).length;
	}

	// A choice reads by name; a per-kg choice (#935, UC-3.R11) adds what it
	// costs on its portion, "مشوي +12.50", since its rate alone says nothing
	// about the line.
	function choiceText(m: { name: string; kind?: string | null; priceDeltaMinor: number }): string {
		if (m.kind !== 'perKg' || m.priceDeltaMinor === 0) return m.name;
		const amount = formatMinor(m.priceDeltaMinor, minorDigits);
		return `${m.name} ${m.priceDeltaMinor > 0 ? `+${amount}` : amount}`;
	}

	function lineUnit(line: DraftLine): number {
		return line.modifiers.reduce((sum, m) => sum + m.priceDeltaMinor, line.unitBaseMinor);
	}

	const types = $derived(
		(
			[
				{ id: 'dine_in', key: 'sellDineIn' },
				{ id: 'takeaway', key: 'sellTakeaway' },
				{ id: 'delivery', key: 'sellDeliveryType' },
			] as const
		).map((option) => ({
			value: option.id,
			label: message(language, option.key),
			testId: `type-${option.id}`,
		})),
	);

	// Every tap counts, the chosen type included: a second tap on Delivery
	// reopens the delivery details, and one on Dine in asks which table.
	function chooseType(type: OrderType) {
		setType(type);
		if (type === 'delivery') onOpenDelivery();
		if (type === 'dine_in') onDineIn();
	}

	// Left-to-right isolate and its end, by code point: the characters
	// themselves are invisible and the compiler rightly refuses them in source.
	const ISOLATE_LTR = String.fromCodePoint(0x2066);
	const POP_ISOLATE = String.fromCodePoint(0x2069);

	// The number as it will be stored, or as typed while it does not read yet.
	function readablePhone(typed: string): string {
		if (!typed) return '';
		try {
			return normalizePhone(typed, countryCode);
		} catch {
			return typed;
		}
	}

	// Where a delivery is going, in one line the cashier can tap to change.
	const deliverySummary = $derived.by(() => {
		const areaName =
			areas.find((area) => area.id === draft.deliveryAreaId)?.name ??
			message(language, 'deliveryNoArea');
		// Isolated left to right, so a leading plus stays in front in Arabic.
		const typed = readablePhone(draft.customerPhone);
		const phone = typed ? `${ISOLATE_LTR}${typed}${POP_ISOLATE}` : '';
		return [areaName, draft.customerName, phone || message(language, 'deliveryPhoneRequired')]
			.filter(Boolean)
			.join(' · ');
	});
</script>

<div class="sell" data-testid="screen-sell">
	{#if !menuLoaded}
		<div class="whole">
			<Loading
				size="lg"
				label={message(language, 'sellLoadingMenu')}
				caption={message(language, 'sellLoadingMenu')}
			/>
		</div>
	{:else if items.length === 0}
		<div class="whole">
			<p class="none">{message(language, 'sellNoMenu')}</p>
		</div>
	{:else}
		<nav class="rail" data-testid="sell-rail">
			<NavItem
				testId="category-mine"
				label={message(language, 'mineTitle')}
				meta={String(mineShown.length)}
				active={category === MINE}
				onPress={() => onSelect(MINE)}
			/>
			<hr class="divider" />
			{#each categories as item (item.id)}
				<NavItem
					testId="category-{item.id}"
					label={item.name}
					meta={String(countIn(item.id))}
					active={item.id === category}
					onPress={() => onSelect(item.id)}
				/>
			{/each}
		</nav>
		<div class="grid-wrap" data-scroll="">
			{#if category === MINE && mineShown.length === 0}
				<div class="mine-empty" data-testid="mine-empty">
					<p class="none">{message(language, 'mineEmpty')}</p>
					<Button
						testId="mine-edit"
						icon="edit"
						label={message(language, 'mineEdit')}
						size="md"
						onPress={onEditMine}
					/>
				</div>
			{:else if category === MINE}
				<div class="mine-head">
					<p class="mine-count" data-testid="mine-count">
						{fill(message(language, 'mineCount'), mineShown.length)}
					</p>
					<Button
						testId="mine-edit"
						icon="edit"
						label={message(language, 'mineEdit')}
						tone="secondary"
						size="sm"
						onPress={onEditMine}
					/>
				</div>
			{/if}
			<div class="grid" data-testid="sell-grid">
				{#each shown as item (item.id)}
					<MenuTile
						testId="tile-{item.id}"
						name={item.name}
						price={tilePrice(language, item, minorDigits)}
						soldOut={!item.available}
						soldOutLabel={message(language, 'sellSoldOut')}
						disabled={pending}
						onAdd={() => add(item)}
					/>
				{/each}
			</div>
		</div>
	{/if}

	<aside class="cart" data-testid="sell-cart">
		<div class="head" inert={locked}>
			{#if openOrder}
				<div class="order" data-testid="cart-order">
					<div class="order-what">
						<span class="order-number n" data-testid="cart-order-number"
							><bdi dir="ltr">{orderNumber(openOrder)}</bdi></span
						>
						<span class="order-meta">{orderMeta}</span>
					</div>
					<Button
						testId="sell-new-order"
						icon="plus"
						label={message(language, 'sellNewOrder')}
						tone="secondary"
						size="sm"
						disabled={unsent > 0 || sending}
						onPress={onNewOrder}
					/>
				</div>
			{:else}
				<Segmented
					label={message(language, 'sellType')}
					options={types}
					value={draft.type}
					wide
					onChange={(value) => chooseType(value as OrderType)}
				/>
			{/if}
			{#if !openOrder && draft.type === 'delivery'}
				<Button
					testId="cart-delivery-details"
					icon="pin"
					label={deliverySummary}
					tone="secondary"
					size="sm"
					wide
					onPress={onOpenDelivery}
				/>
			{/if}
			{#if !openOrder && draft.type === 'delivery' && driverName}
				<Button
					testId="cart-driver"
					label={fill(message(language, 'driverLabel'), driverName)}
					trailingIcon="edit"
					tone="secondary"
					size="sm"
					wide
					onPress={onOpenDrivers}
				/>
			{/if}
			{#if !openOrder && draft.type === 'dine_in' && tableName}
				<Button
					testId="cart-table"
					label={fill(message(language, 'tableLabel'), tableName)}
					trailingIcon="edit"
					tone="secondary"
					size="sm"
					wide
					onPress={onOpenTables}
				/>
			{:else if !openOrder && tableAsk}
				<Button
					testId="cart-table-pick"
					label={message(language, 'tableTitle')}
					trailingIcon="expand"
					tone="secondary"
					size="sm"
					wide
					onPress={onOpenTables}
				/>
			{/if}
		</div>
		{#if billChanged}
			<div class="bill-mark">
				<Band tone="flag" testId="sell-bill-changed">{message(language, 'sellBillChanged')}</Band>
			</div>
		{/if}
		<!-- A list the page owns: the lines scroll, and the totals and Pay stay
		     outside what scrolls. -->
		<div class="lines" data-testid="cart-lines" data-scroll="" role="list" inert={locked}>
			{#if draft.lines.length === 0 && sentLines.length === 0}
				<p class="empty">{message(language, 'sellEmpty')}</p>
			{/if}
			{#each openOrder?.lines ?? [] as line, i (i)}
				<CartLine
					lineKey="sent-{i}"
					qty={line.qty}
					name={lineName(language, line.name, line.grams)}
					modifiers={(line.modifiers ?? []).map(choiceText)}
					note={line.note ?? ''}
					total={formatMinor(line.lineTotalMinor, minorDigits)}
					removeLabel={message(language, 'sellRemove')}
					sent
					sentLabel={message(language, 'sellSent')}
					onOpen={line.lineId ? () => onCancelLine(line) : undefined}
				/>
			{/each}
			{#each draft.lines as line (line.key)}
				<CartLine
					lineKey={line.key}
					qty={line.qty}
					name={lineName(language, line.name, line.weight?.grams)}
					modifiers={line.modifiers.map(choiceText)}
					note={line.note}
					total={formatMinor(line.qty * lineUnit(line), minorDigits)}
					removeLabel={message(language, 'sellRemove')}
					onOpen={() => onEditLine(line)}
					onRemove={() => onRemoveLine(line)}
				/>
			{/each}
		</div>
		<div class="foot">
			{#if pending}
				<div class="unsent" role="alert" data-testid="sell-pending">
					<span class="unsent-title">{message(language, 'sellPendingTitle')}</span>
					<span class="unsent-body">{message(language, 'sellPendingBody')}</span>
					<Button
						testId="pending-retry"
						label={message(language, 'sellPendingRetry')}
						tone="ink"
						size="md"
						wide
						state={retrying ? 'pending' : 'idle'}
						pendingLabel={message(language, 'sellSending')}
						onPress={onRetryPending}
					/>
				</div>
			{/if}
			<div class="give">
				<Button
					testId="sell-discount"
					icon="percent"
					label={message(language, 'sellDiscount')}
					tone="secondary"
					size="sm"
					disabled={(draft.lines.length === 0 && !openOrder) || locked}
					onPress={onDiscount}
				/>
			</div>
			<p class="sum">
				<span>{message(language, 'sellSubtotal')}</span>
				<output class="n" dir="ltr" data-testid="cart-subtotal">
					{formatMinor(totals?.subtotalMinor ?? 0, minorDigits)}
				</output>
			</p>
			{#if typeOf === 'delivery'}
				<p class="sum">
					<span>{message(language, 'sellDelivery')}</span>
					<output class="n" dir="ltr" data-testid="cart-delivery">
						{formatMinor(totals?.deliveryMinor ?? deliveryFeeMinor, minorDigits)}
					</output>
				</p>
			{/if}
			{#if discount}
				<p class="sum" data-testid="cart-discount">
					<span class="what">{discountLabel(language, discount, minorDigits)}</span>
					<output class="n off" dir="ltr" data-testid="cart-discount-amount">
						{formatMinor(-(totals?.discountMinor ?? 0), minorDigits)}
					</output>
				</p>
			{/if}
			<p class="total">
				<span>{message(language, 'sellTotal')}</span>
				<output class="n" dir="ltr" data-testid="cart-total">{total}</output>
			</p>
			{#if blocked}
				<p class="blocked" role="alert" data-testid="cart-discount-blocked">
					{message(language, 'discountManagerOnly')}
				</p>
			{/if}
			{#if tableNotice}
				<p class="blocked" role="alert" data-testid="cart-table-notice">{tableNotice}</p>
			{/if}
			{#if driverNotice}
				<p class="blocked" role="alert" data-testid="cart-driver-notice">{driverNotice}</p>
			{/if}
			{#if !pending || printingBill}
				<div class="act">
					{#if openOrder && unsent === 0}
						<Button
							testId="sell-print-bill"
							icon="printer"
							label={message(language, 'sellPrintBill')}
							tone="secondary"
							size="lg"
							wide
							state={printingBill ? 'pending' : 'idle'}
							pendingLabel={message(language, 'sellBillPending')}
							disabled={locked || paying || printingBill || billHeld}
							onPress={onPrintBill}
						/>
					{:else}
						<Button
							testId="sell-send"
							icon="send"
							label={message(language, 'sellSend')}
							size="lg"
							wide
							state={sending && sendSlow ? 'pending' : 'idle'}
							pendingLabel={message(language, 'sellSending')}
							disabled={sendOff}
							onPress={onSend}
						/>
					{/if}
					<div class="pay">
						<Button
							testId="sell-pay"
							label="{message(language, 'sellPay')} {total}"
							size="lg"
							wide
							disabled={payOff}
							onPress={onPay}
						/>
						{#if unsent > 0 && !blocked}
							<span class="why" data-testid="sell-send-first"
								>{message(language, 'sellSendFirst')}</span
							>
						{/if}
					</div>
				</div>
			{/if}
		</div>
	</aside>
</div>

<style>
	.sell {
		flex: 1;
		min-block-size: 0;
		min-inline-size: 0;
		display: flex;
	}

	.whole {
		flex: 1;
		min-inline-size: 0;
		display: grid;
		place-items: center;
		padding: var(--space-9);
	}

	.none {
		margin: 0;
		font-size: var(--text-md);
		color: var(--text-body);
	}

	.rail {
		flex: none;
		inline-size: var(--till-rail);
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
		padding: var(--space-5) var(--space-3);
		border-inline-end: var(--border-width) solid var(--border-default);
	}

	.grid-wrap {
		flex: 1;
		min-inline-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		padding: var(--space-5);
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.divider {
		margin: var(--space-2) var(--space-3);
		border: 0;
		border-block-start: var(--border-width) solid var(--border-default);
	}

	.mine-empty {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--space-7);
		text-align: center;
	}

	.mine-head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--space-5);
	}

	.mine-count {
		margin: 0;
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(var(--tile-min), 1fr));
		gap: var(--touch-gap);
	}

	.cart {
		flex: none;
		inline-size: var(--till-cart);
		min-block-size: 0;
		display: flex;
		flex-direction: column;
		background: var(--surface-card);
		border-inline-start: var(--border-width) solid var(--border-default);
	}

	.head {
		flex: none;
		padding: var(--space-5);
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
		border-block-end: var(--border-width) solid var(--border-inset);
	}

	.lines {
		flex: 1;
		min-block-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
	}

	.empty {
		margin: 0;
		padding: var(--space-12) var(--space-9);
		text-align: center;
		font-size: var(--text-base);
		color: var(--text-muted);
	}

	.foot {
		flex: none;
		padding: var(--space-5);
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
		border-block-start: var(--border-width) solid var(--border-default);
	}

	.unsent {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
		padding: var(--space-5);
		background: var(--surface-flag);
		border: var(--border-width) solid var(--border-flag);
		border-radius: var(--radius-input);
	}

	.unsent-title {
		font-size: var(--text-sm);
		font-weight: 700;
		color: var(--ink-flag-deep);
	}

	.unsent-body {
		font-size: var(--text-xs);
		color: var(--text-body);
	}

	.give {
		display: flex;
		align-items: center;
		gap: var(--space-4);
	}

	.sum {
		margin: 0;
		display: flex;
		justify-content: space-between;
		gap: var(--space-4);
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.what {
		min-inline-size: 0;
	}

	.off {
		flex: none;
	}

	.order {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		min-block-size: var(--ctl-min);
	}

	.order-what {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
	}

	.order-number {
		font-size: var(--text-md);
		font-weight: 700;
		color: var(--text-strong);
	}

	.order-meta {
		font-size: var(--text-sm);
		color: var(--text-body);
		overflow-wrap: anywhere;
	}

	.act {
		display: grid;
		grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
		gap: var(--touch-gap);
		align-items: start;
	}

	.pay {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.why {
		font-size: var(--text-sm);
		font-weight: 600;
		color: var(--text-strong);
		text-align: center;
	}

	.blocked {
		margin: 0;
		font-size: var(--text-sm);
		font-weight: 600;
		line-height: var(--leading-snug);
		color: var(--ink-flag-deep);
	}

	.total {
		margin: 0;
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		font-size: var(--text-base);
		font-weight: 700;
	}

	.n {
		font-family: var(--font-mono);
	}

	.total .n {
		font-size: var(--figure-sm);
	}
</style>
